// src/pages/pranayamma/ViewPranayamaPage.jsx
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
  alpha,
  Stack,
  Divider,
  Tooltip
} from '@mui/material';
import {
  SelfImprovementOutlined,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import CreatePranayamaDialog from 'components/openDialog/CreatePranayama';
import EditPranayamaDialog from 'components/openDialog/EditPranayamaDialog';
import { getPranayammas } from '../../services/pranayamma/getPranayammas';
import { deletePranayamma } from 'services/pranayamma/deletePranayamma';



function ViewPranayamaPage() {
  const { courseid } = useParams();
  console.log("courseid",courseid)
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPranayama, setSelectedPranayama] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pranayamaToDelete, setPranayamaToDelete] = useState(null);

  

  const {
    data: apiResponse,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['pranayammas', courseid],
    queryFn: () => getPranayammas({ courseid: Number(courseid) }),
    enabled: !!courseid
  });

  const pranayammas = apiResponse?.data || [];

  const handlePranayamaCreated = () => {
queryClient.invalidateQueries(['pranayammas', courseid]);    setCreateOpen(false);
  };

  const handleOpenEdit = (p) => {
    setSelectedPranayama(p);
    setEditOpen(true);
  };

  const handleOpenDelete = (p) => {
    setPranayamaToDelete(p);
    setDeleteDialogOpen(true);
  };

 const deleteMutation = useMutation({
  mutationFn: deletePranayamma,

  onSuccess: (data) => {
  queryClient.invalidateQueries({ queryKey: ['pranayammas', courseid] });
  // Force immediate refetch of this exact query
  queryClient.refetchQueries({ queryKey: ['pranayammas', courseid], exact: true });
  toast.success(data?.message || 'Pranayama deleted successfully');
  setDeleteDialogOpen(false);
},
  onError: (err) => {
    toast.error(err?.message || 'Failed to delete pranayama');
  },
});

  const handleConfirmDelete = () => {
  if (!pranayamaToDelete?.id) return;
  deleteMutation.mutate({ pranayammaId: pranayamaToDelete.id });
  // Do NOT put invalidate here
};

  return (
    <Box >
     

      
      {pranayammas.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 12 }}>
          <SelfImprovementOutlined sx={{ fontSize: 80, color: 'action.disabled', mb: 3 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No pranayama practices added yet for this Course
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ mt: 2 }}
          >
            Add First Practice
          </Button>
        </Box>
      ) : (
       <Grid container spacing={3}>
  {pranayammas.map((p) => {
    let sequence = [];
    let benefits = [];

    try {
      sequence = typeof p.breathing_sequence === 'string'
        ? JSON.parse(p.breathing_sequence)
        : Array.isArray(p.breathing_sequence) ? p.breathing_sequence : [];
    } catch {}

    try {
      benefits = typeof p.benefits === 'string'
        ? JSON.parse(p.benefits)
        : Array.isArray(p.benefits) ? p.benefits : [];
    } catch {}

    const difficultyColor = {
      Beginner:   'success',
      Intermediate: 'warning',
      Advanced:   'error'
    }[p.difficulty_level || 'Beginner'];

    return (
      <Grid item xs={12} sm={6} lg={4} key={p.id}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              borderColor: `${difficultyColor}.main`,
              boxShadow: `0 0 0 1px ${difficultyColor}.main`,
            }
          }}
        >
          {/* Top accent bar + title */}
          <Box
            sx={{
              height: 6,
              bgcolor: `${difficultyColor}.main`,
              width: '100%'
            }}
          />

          <CardContent sx={{ p: 3, pb: 2, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography
                variant="h6"
                component="div"
                fontWeight={600}
                sx={{ pr: 2, lineHeight: 1.3 }}
              >
                {p.name}
              </Typography>

              <Chip
                label={p.difficulty_level || 'Beginner'}
                size="small"
                color={difficultyColor}
                variant="filled"
                sx={{
                  fontWeight: 600,
                  minWidth: 88,
                  height: 24,
                  fontSize: '0.75rem'
                }}
              />
            </Box>

            {/* Quick stats */}
            <Stack
              direction="row"
              divider={<Box sx={{ width: 1, height: 16, bgcolor: 'divider', mx: 1.5 }} />}
              spacing={2}
              sx={{ mb: 2.5 }}
            >
              <Stack alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                  Duration
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {p.duration ? `${p.duration}s` : '—'}
                </Typography>
              </Stack>

              <Stack alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                  Rounds
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {p.loops || '—'}
                </Typography>
              </Stack>
            </Stack>

            {/* Breathing sequence */}
            {sequence.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                  sx={{ display: 'block', mb: 1 }}
                >
                  BREATHING FLOW
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {sequence.map((s, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 12,
                        bgcolor: s?.type?.includes('in')  ? 'success.50' :
                                 s?.type?.includes('ex')  ? 'error.50'   :
                                 'warning.50',
                        color: s?.type?.includes('in')  ? 'success.dark' :
                               s?.type?.includes('ex')  ? 'error.dark'   :
                               'warning.dark',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        border: '1px solid',
                        borderColor: s?.type?.includes('in')  ? 'success.light' :
                                     s?.type?.includes('ex')  ? 'error.light'   :
                                     'warning.light'
                      }}
                    >
                      {s?.type || '—'} {s?.seconds || '?'}s
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Description */}
            {p.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.65,
                  mb: 2.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {p.description}
              </Typography>
            )}

            {/* Benefits */}
            {benefits.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                  sx={{ display: 'block', mb: 1 }}
                >
                  KEY BENEFITS
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {benefits.slice(0, 5).map((b, idx) => (
                    <Chip
                      key={idx}
                      label={b}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.775rem',
                        height: 26,
                        borderColor: 'success.light',
                        color: 'success.main'
                      }}
                    />
                  ))}
                  {benefits.length > 5 && (
                    <Chip
                      label={`+${benefits.length - 5}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.775rem',
                        height: 26
                      }}
                    />
                  )}
                </Stack>
              </Box>
            )}
          </CardContent>

          {/* Actions bar */}
          <Box
            sx={{
              px: 3,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1
            }}
          >
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenEdit(p)}
              sx={{ '&:hover': { bgcolor: 'primary.50' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              color="error"
              onClick={() => handleOpenDelete(p)}
              sx={{ '&:hover': { bgcolor: 'error.50' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Card>
      </Grid>
    );
  })}
</Grid>
      )}

      {/* Dialogs remain unchanged */}
      <CreatePranayamaDialog open={createOpen} onClose={() => setCreateOpen(false)} onSave={handlePranayamaCreated} />

      <EditPranayamaDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelectedPranayama(null);
        }}
        pranayama={selectedPranayama}
        onSave={() => {
          queryClient.invalidateQueries(['pranayammas', courseid]);
          toast.success('Updated successfully');
        }}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Pranayama</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{pranayamaToDelete?.name || 'this practice'}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ViewPranayamaPage;